// VehiculoConductores.js
import React, { useState, useEffect, useCallback } from "react";
import { Spin, notification } from "antd";
import Dashboard from "./Dashboard";
import TablaAsignaciones from "./TablaAsignaciones";
import AsignarDialog from "./AsignarDialog";
import AsignarBuscadorFiltro from "./AsignarBuscadorFiltro";
import ConfirmDialog from "./ConfirmDialog";
import HistorialAsignacionesDialog from "./HistorialAsignacionesDialog";
import {
  fetchAsignaciones,
  fetchVehiculos,
  fetchConductores,
  fetchZonas,
  crearAsignacion,
  eliminarAsignacion,
} from "./apiService";
import { calcularStats } from "./statsUtils";
import { useUser } from "../../../UserContext";

const VehiculoConductores = () => {
  const [asignaciones, setAsignaciones] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [asignarDialogOpen, setAsignarDialogOpen] = useState(false);
  const [historialDialogOpen, setHistorialDialogOpen] = useState(false);
  const [confirmacion, setConfirmacion] = useState(false);
  const [zonaFiltro, setZonaFiltro] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [asignacionPendiente, setAsignacionPendiente] = useState(null);
  const [currentPrincipal, setCurrentPrincipal] = useState(null);

  const { user: currentUser } = useUser();

  const fetchData = useCallback(async () => {
    try {
      const [asignacionesRes, vehiculosRes, conductoresRes, zonasRes] =
        await Promise.all([
          fetchAsignaciones(),
          fetchVehiculos(),
          fetchConductores(),
          fetchZonas(),
        ]);

      const zonasData = zonasRes.data.zonas || [];
      const vehiculosConZonas = mapWithZones(vehiculosRes.data, zonasData);
      const conductoresConZonas = mapWithZones(
        conductoresRes.data,
        zonasData
      );

      // Mapear las asignaciones para incluir Vehiculo y Conductor completos
      const asignacionesConDatos = asignacionesRes.data.map((asignacion) => {
        const vehiculo = vehiculosConZonas.find(
          (v) => v.id === asignacion.vehiculo_id
        );
        const conductor = conductoresConZonas.find(
          (c) => c.id === asignacion.conductor_id
        );
        return {
          ...asignacion,
          Vehiculo: vehiculo || {},
          Conductor: conductor || {},
        };
      });

      setAsignaciones(asignacionesConDatos);
      setVehiculos(vehiculosConZonas);
      setConductores(conductoresConZonas);
      setZonas(zonasData);

      const newStats = calcularStats(
        asignacionesConDatos,
        vehiculosConZonas,
        conductoresConZonas
      );
      setStats(newStats);
      setLoading(false);
    } catch (error) {
      console.error("Error al obtener datos:", error);
      notification.error({
        message: "Error",
        description: "Error al obtener datos.",
        placement: "topRight",
      });
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const mapWithZones = (items, zonas) => {
    return items.map((item) => {
      const zona = zonas.find(
        (zona) => String(zona.id) === String(item.zona_id)
      );
      return { ...item, zona: zona || {} };
    });
  };

  const actualizarStats = useCallback(
    (nuevasAsignaciones) => {
      const newStats = calcularStats(
        nuevasAsignaciones,
        vehiculos,
        conductores
      );
      setStats(newStats);
    },
    [vehiculos, conductores]
  );

  // VehiculoConductores.js
const handleAsignar = async (asignacion) => {
  try {
    // Obtener el ID del usuario actual
    const usuarioId = currentUser?.id;
    
    console.log("=== INICIO handleAsignar ===");
    console.log("Datos de asignación recibidos:", asignacion);
    console.log("Usuario ID obtenido:", usuarioId);
    
    if (!usuarioId) {
      console.error("Usuario ID no disponible - no se puede crear historial");
      notification.error({
        message: "Error",
        description: "No se pudo identificar al usuario. Por favor, inicie sesión nuevamente.",
        placement: "topRight",
      });
      return;
    }

    console.log("Enviando solicitud POST a /vehiculo-conductores");
    console.log("Datos completos a enviar:", { ...asignacion, usuario_id: usuarioId });
    
    const res = await crearAsignacion(asignacion, usuarioId);
    
    console.log("Respuesta del servidor:", res.data);
    
    actualizarAsignaciones(
      asignacion,
      res.data.vehiculoConductor,
      res.data.currentPrincipal
    );
    setAsignarDialogOpen(false);
  } catch (error) {
    console.error(">>> ERROR COMPLETO EN handleAsignar <<<");
    console.error("Error objeto:", error);
    console.error("Error response:", error.response);
    console.error("Error message:", error.message);
    
    const backendMsg = error.response?.data?.mensaje || error.message || "";
    
    if (backendMsg.includes("principal en otro vehículo")) {
      return notification.error({
        message: "Error de asignación",
        description: backendMsg,
        placement: "topRight",
      });
    }
    handleError(error, "Error al crear asignación");
  }
};

  const actualizarAsignaciones = (
    asignacion,
    vehiculoConductor,
    currentPrincipal
  ) => {
    // Si hay un error, no procesar la confirmación
    if (vehiculoConductor && vehiculoConductor.error) {
      return; 
    }
    const vehiculo = vehiculos.find((v) => v.id === asignacion.vehiculo_id);
    const conductor = conductores.find((c) => c.id === asignacion.conductor_id);

    setAsignaciones((prev) => {
      const updatedAsignaciones = updatePrincipalState(prev, asignacion);
      const isNewAsignacion = checkIfNewAsignacion(prev, asignacion);

      if (isNewAsignacion) {
        updatedAsignaciones.push({
          ...vehiculoConductor,
          Vehiculo: vehiculo,
          Conductor: conductor,
        });
      }

      actualizarStats(updatedAsignaciones);
      return updatedAsignaciones;
    });

    if (currentPrincipal) {
      setCurrentPrincipal(currentPrincipal);
      notification.info({
        message: "Aviso",
        description: `Este vehículo ya tiene un conductor principal (${currentPrincipal.Conductor.nombre}). Será reemplazado.`,
        placement: "topRight",
      });
      setAsignacionPendiente(asignacion);
      setConfirmacion(true);
    } else {
      notification.success({
        message: "Éxito",
        description: "Asignación creada exitosamente.",
        placement: "topRight",
      });
      setAsignarDialogOpen(false);
    }
  };

  const updatePrincipalState = (prev, asignacion) => {
    return prev.map((a) => {
      if (a.es_principal && a.vehiculo_id === asignacion.vehiculo_id) {
        return { ...a, es_principal: false };
      }
      return a;
    });
  };

  const checkIfNewAsignacion = (prev, asignacion) => {
    return !prev.some(
      (a) =>
        a.vehiculo_id === asignacion.vehiculo_id &&
        a.conductor_id === asignacion.conductor_id
    );
  };

  const handleConfirmacion = async () => {
    setConfirmacion(false);
    if (asignacionPendiente) {
      try {
        const res = await crearAsignacion(asignacionPendiente);
        actualizarAsignaciones(asignacionPendiente, res.data.vehiculoConductor);
        notification.success({
          message: "Éxito",
          description: "Asignación confirmada exitosamente.",
          placement: "topRight",
        });
      } catch (error) {
        handleError(error, "Error al confirmar la asignación");
      }
    }
  };

  const handleError = (error, defaultMessage) => {
    console.error("Error completo:", error);

    // Verificar si el mensaje de error contiene la cadena específica
    const errorMessage = error.message || "";
    const responseMessage =
      (error.response && error.response.data && error.response.data.mensaje) ||
      "";

    if (
      errorMessage.includes("principal en otro vehículo") ||
      responseMessage.includes("principal en otro vehículo")
    ) {
      return notification.error({
        message: "Error de asignación",
        description: "El conductor ya es principal en otro vehículo",
        placement: "topRight",
      });
    }
    // 1. Verificar si es un error de red
    if (errorMessage === "Network Error") {
      return notification.error({
        message: "Error de red",
        description: "No se pudo conectar al servidor",
        placement: "topRight",
      });
    }
    // 2. Verificar si hay respuesta del servidor
    if (error.response) {
      const { data, status } = error.response;
      // Manejar diferentes códigos de estado
      switch (status) {
        case 400:
        case 500:
          return notification.error({
            message: `Error ${status}`,
            description: data.mensaje || data.error || defaultMessage,
            placement: "topRight",
          });
        default:
          return notification.error({
            message: `Error ${status}`,
            description: defaultMessage,
            placement: "topRight",
          });
      }
    }
    // 3. Manejar errores inesperados
    return notification.error({
      message: "Error",
      description: defaultMessage,
      placement: "topRight",
    });
  };

  const handleSearchTermChange = (event) =>
    setSearchTerm(event.target.value.trim());

  const handleZonaFiltroChange = (value) => {
    setZonaFiltro(value === -1 ? null : value);
  };

  const handleDeleteAsignacion = async (id) => {
    try {
      const usuarioId = currentUser?.id;

      console.log("=== INICIO handleDeleteAsignacion ===");
    console.log("Asignación ID:", id);
    console.log("Usuario ID:", usuarioId);
    console.log("Usuario objeto completo:", currentUser);

      if (!usuarioId){
        console.error("Usuario ID no disponible");
        notification.error({
          message: "Error",
          description: " No se pudo identificar al usuario. Por favor, inicie sesion nuevamente.",
          placement: "topRight",
        });
        return;
      }

      console.log("Enviando solicitud DELETE a:", `/vehiculo-conductores/${id}`);
    console.log("Con datos:", { usuario_id: usuarioId });

      console.log("Eliminando asignacion con ID:", id);
      console.log("Usuario ID:", usuarioId);
      
      
      await eliminarAsignacion(id, usuarioId);

      console.log("Asignación eliminada exitosamente");
      // Actualizar el estado de las asignaciones
      const asignacionesRes = await fetchAsignaciones();
      const asignacionesConDatos = asignacionesRes.data.map((asignacion) => {
        const vehiculo = vehiculos.find((v) => v.id === asignacion.vehiculo_id);
        const conductor = conductores.find(
          (c) => c.id === asignacion.conductor_id
        );
        return {
          ...asignacion,
          Vehiculo: vehiculo,
          Conductor: conductor,
        };
      });
      setAsignaciones(asignacionesConDatos);
      // Recalcular estadísticas
      setStats(calcularStats(asignacionesConDatos, vehiculos, conductores));

      notification.success({
        message: "Éxito",
        description: "Asignación eliminada correctamente",
        placement: "topRight",
      });
    } catch (error) {
      console.log("Error al eliminar asignación:", error);
      handleError(error, "Error al eliminar la asignación");
    }
  };

  if (loading) return <Spin />;

  return (
    <div>
      <Dashboard stats={stats} />
      <AsignarBuscadorFiltro
        onAsignarClick={() => setAsignarDialogOpen(true)}
        onHistorialClick={() => setHistorialDialogOpen(true)}
        searchTerm={searchTerm}
        handleSearchTermChange={handleSearchTermChange}
        zonaFiltro={zonaFiltro}
        handleZonaFiltroChange={handleZonaFiltroChange}
        zonas={zonas}
      />
      <TablaAsignaciones
        asignaciones={asignaciones}
        zonaFiltro={zonaFiltro}
        searchTerm={searchTerm}
        onDeleteAsignacion={handleDeleteAsignacion}
      />
      <AsignarDialog
        open={asignarDialogOpen}
        onClose={() => setAsignarDialogOpen(false)}
        vehiculos={vehiculos}
        conductores={conductores}
        zonas={zonas}
        onSubmit={handleAsignar}
        asignaciones={asignaciones}
        setConfirmacion={setConfirmacion}
        setAsignacionPendiente={setAsignacionPendiente}
        currentPrincipal={currentPrincipal}
        setCurrentPrincipal={setCurrentPrincipal}
      />
      <ConfirmDialog
        open={confirmacion}
        onClose={() => setConfirmacion(false)}
        onConfirm={handleConfirmacion}
        aviso={`Este vehículo ya tiene un conductor principal (${currentPrincipal?.Conductor?.nombre}). Será reemplazado.`}
        currentPrincipal={currentPrincipal}
      />
      <HistorialAsignacionesDialog
        open={historialDialogOpen}
        onClose={() => setHistorialDialogOpen(false)}
      />
    </div>
  );
};

export default VehiculoConductores;
